import { useCallback, useEffect } from "react";

import { $isLinkNode } from "@lexical/link";
import { $isListNode } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $findMatchingParent } from "@lexical/utils";
import { $getSelection, $isRangeSelection, $isRootNode, COMMAND_PRIORITY_LOW, SELECTION_CHANGE_COMMAND } from "lexical";

import type { RTEComponentDefs } from "../RichTextEditorProps";

export type RTEActiveFormatDefs = {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
  readonly strikethrough: boolean;
  readonly unorderedList: boolean;
  readonly orderedList: boolean;
  readonly link: boolean;
};

export const EMPTY_ACTIVE_FORMATS: RTEActiveFormatDefs = {
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  unorderedList: false,
  orderedList: false,
  link: false,
};

export interface ActiveFormatsPluginDefs {
  readonly onFormatsChange: (formats: RTEActiveFormatDefs) => void;
  readonly onLinkEditRequest: (url: string | null) => void;
}

export const ActiveFormatsPlugin: RTEComponentDefs<ActiveFormatsPluginDefs> = ({ onFormatsChange, onLinkEditRequest }) => {
  const [editor] = useLexicalComposerContext();
  const publishActiveFormats = useCallback(() => {
    const clearFormats = () => {
      onFormatsChange(EMPTY_ACTIVE_FORMATS);
      onLinkEditRequest(null);
    };
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      clearFormats();
      return;
    }
    const anchorNode = selection.anchor.getNode();
    if ($isRootNode(anchorNode)) {
      clearFormats();
      return;
    }
    const topLevelNode = anchorNode.getTopLevelElementOrThrow();
    const linkNode = $findMatchingParent(anchorNode, $isLinkNode);
    const listType = $isListNode(topLevelNode) ? topLevelNode.getListType() : null;
    onFormatsChange({
      bold: selection.hasFormat("bold"),
      italic: selection.hasFormat("italic"),
      underline: selection.hasFormat("underline"),
      strikethrough: selection.hasFormat("strikethrough"),
      unorderedList: listType === "bullet",
      orderedList: listType === "number",
      link: linkNode !== null,
    });
    onLinkEditRequest($isLinkNode(linkNode) ? linkNode.getURL() : null);
  }, [onFormatsChange, onLinkEditRequest]);

  useEffect(() => {
    const unsubscribeSelection = editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        publishActiveFormats();
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
    const unsubscribeUpdates = editor.registerUpdateListener(({ editorState }) => editorState.read(publishActiveFormats));
    return () => {
      unsubscribeSelection();
      unsubscribeUpdates();
    };
  }, [editor, publishActiveFormats]);

  return null;
};
